/**
 * useNativeApp Hook
 * Initialize all native services and provide status
 */

import { useState, useEffect, useCallback } from 'react';
import { App } from '@capacitor/app';
import { platformService } from '../services/native/platformService';
import { pushNotificationService } from '../services/native/pushNotificationService';
import { networkService } from '../services/native/networkService';
import { storageService } from '../services/native/storageService';

export function useNativeApp() {
  const [initialized, setInitialized] = useState(false);
  const [isNative, setIsNative] = useState(false);
  const [platform, setPlatform] = useState('web');
  const [networkStatus, setNetworkStatus] = useState({ connected: true, connectionType: 'unknown' });
  const [pushEnabled, setPushEnabled] = useState(false);
  
  // Initialize all native services
  useEffect(() => {
    const initializeNativeServices = async () => {
      // Set platform info
      setIsNative(platformService.isNative());
      setPlatform(platformService.getPlatform());
      
      // Initialize network monitoring
      await networkService.initialize();
      const status = await networkService.getStatus();
      setNetworkStatus(status);
      
      // Subscribe to network changes
      networkService.onStatusChange(setNetworkStatus);
      
      // Initialize push notifications on native platforms
      if (platformService.isNative()) {
        const pushResult = await pushNotificationService.initialize();
        setPushEnabled(pushResult);
        
        // Handle app state changes (foreground/background)
        App.addListener('appStateChange', (state) => {
          console.log('App state changed:', state.isActive);
          if (state.isActive) {
            // App came to foreground - refresh data if needed
            window.dispatchEvent(new CustomEvent('appResumed'));
          }
        });
        
        // Handle deep links
        App.addListener('appUrlOpen', (event) => {
          console.log('App URL opened:', event.url);
          handleDeepLink(event.url);
        });
        
        // Handle back button on Android
        App.addListener('backButton', (event) => {
          console.log('Back button pressed');
          // Let the router handle it by default
        });
      }
      
      setInitialized(true);
    };
    
    initializeNativeServices();
    
    // Cleanup
    return () => {
      networkService.cleanup();
      pushNotificationService.cleanup();
    };
  }, []);
  
  // Handle deep links for navigation
  const handleDeepLink = useCallback((url) => {
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname;
      
      // Navigate based on deep link path
      if (path.startsWith('/my-matches/')) {
        window.location.href = path;
      } else if (path === '/quiz') {
        window.location.href = '/quiz';
      } else if (path === '/favorites') {
        window.location.href = '/favorites';
      }
    } catch (error) {
      console.error('Invalid deep link URL:', error);
    }
  }, []);
  
  // Utility functions
  const checkNetwork = useCallback(async () => {
    return networkService.getStatus();
  }, []);
  
  const saveToLocalStorage = useCallback(async (key, value) => {
    return storageService.set(key, value);
  }, []);
  
  const getFromLocalStorage = useCallback(async (key) => {
    return storageService.get(key);
  }, []);
  
  return {
    // State
    initialized,
    isNative,
    platform,
    networkStatus,
    pushEnabled,
    
    // Computed
    isOnline: networkStatus.connected,
    isIOS: platform === 'ios',
    isAndroid: platform === 'android',
    isWeb: platform === 'web',
    
    // Methods
    checkNetwork,
    saveToLocalStorage,
    getFromLocalStorage,
    
    // Services (direct access)
    services: {
      push: pushNotificationService,
      network: networkService,
      storage: storageService
    }
  };
}

export default useNativeApp;
